import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AppShell from '../components/AppShell';
import { useAppBadge } from '../hooks/useAppBadge';
import { docsPath } from '../hooks/useGuidedDocuments';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const ALERT_ICONS = {
  welcome:               { icon: '👋', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500',   header: '#3a6ad9' },
  application_received:  { icon: '📋', bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-500',  header: '#acf44e' },
  status_changed:        { icon: '🔄', bg: 'bg-purple-50', border: 'border-purple-200', dot: 'bg-purple-500', header: '#7c3aed' },
  document_approved:     { icon: '✅', bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-500',  header: '#acf44e' },
  document_rejected:     { icon: '⚠️', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500',    header: '#ff082f' },
  condition_raised:      { icon: '📌', bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500', header: '#ea580c' },
  showing_confirmed:     { icon: '🏡', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500',   header: '#3a6ad9' },
  offer_update:          { icon: '📝', bg: 'bg-indigo-50', border: 'border-indigo-200', dot: 'bg-indigo-500', header: '#4338ca' },
  option_period_expiring:{ icon: '⏰', bg: 'bg-yellow-50', border: 'border-yellow-200', dot: 'bg-yellow-500', header: '#d97706' },
  clear_to_close:        { icon: '🚀', bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-500',  header: '#acf44e' },
  closing_scheduled:     { icon: '📅', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500',   header: '#3a6ad9' },
  wire_instructions:     { icon: '🏦', bg: 'bg-gray-50', border: 'border-gray-200', dot: 'bg-gray-500',   header: '#374151' },
  coborrower_pending:    { icon: '👥', bg: 'bg-yellow-50', border: 'border-yellow-200', dot: 'bg-yellow-500', header: '#d97706' },
  docs_reminder:         { icon: '📄', bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500', header: '#ea580c' },
  document_request:      { icon: '📎', bg: 'bg-blue-50',   border: 'border-blue-200',   dot: 'bg-blue-500',   header: '#2563EB' },
  // ReadyCheck flow alerts — fire from readycheck.js + review.js at each
  // milestone in the verification → in_review pipeline. Without these
  // entries the alert renders with the gray DEFAULT_STYLE and no CTA.
  readycheck_phase1_run:                         { icon: '⚡', bg: 'bg-blue-50',   border: 'border-blue-200',   dot: 'bg-blue-500',   header: '#3a6ad9' },
  readycheck_income_confirmed:                   { icon: '✅', bg: 'bg-green-50',  border: 'border-green-200',  dot: 'bg-green-500',  header: '#acf44e' },
  readycheck_income_disputed:                    { icon: '⚠️', bg: 'bg-yellow-50', border: 'border-yellow-200', dot: 'bg-yellow-500', header: '#d97706' },
  readycheck_phase3_credit_pulled:               { icon: '📊', bg: 'bg-blue-50',   border: 'border-blue-200',   dot: 'bg-blue-500',   header: '#3a6ad9' },
  readycheck_submitted:                          { icon: '🎉', bg: 'bg-green-50',  border: 'border-green-200',  dot: 'bg-green-500',  header: '#acf44e' },
  readycheck_primary_submitted_awaiting_co:      { icon: '⏳', bg: 'bg-yellow-50', border: 'border-yellow-200', dot: 'bg-yellow-500', header: '#d97706' },
  readycheck_coborrower_submitted_awaiting_primary: { icon: '⏳', bg: 'bg-yellow-50', border: 'border-yellow-200', dot: 'bg-yellow-500', header: '#d97706' },
  readycheck_awaiting_coborrower:                { icon: '⏳', bg: 'bg-yellow-50', border: 'border-yellow-200', dot: 'bg-yellow-500', header: '#d97706' },
  review_section_accepted:                       { icon: '✅', bg: 'bg-green-50',  border: 'border-green-200',  dot: 'bg-green-500',  header: '#acf44e' },
  review_section_flagged:                        { icon: '⚠️', bg: 'bg-red-50',    border: 'border-red-200',    dot: 'bg-red-500',    header: '#ff082f' },
  additional_need:                               { icon: '📋', bg: 'bg-yellow-50', border: 'border-yellow-200', dot: 'bg-yellow-500', header: '#d97706' },
  credit_unlocked:                               { icon: '🔓', bg: 'bg-blue-50',   border: 'border-blue-200',   dot: 'bg-blue-500',   header: '#3a6ad9' },
};

const DEFAULT_STYLE = { icon: '🔔', bg: 'bg-gray-50', border: 'border-gray-200', dot: 'bg-gray-400', header: '#374151' };

// 🔑 Every borrower's documents are on /documents (cleanup Phase 2, 2026-10-28/29 — the Verification page is gone).
// A stored alert whose own url still says /verification is mapped at click time by docsPath.
const DOCUMENTS_URL = '/documents#action-needed';

const ALERT_ACTIONS = {
  welcome:                { url: '/dashboard',                  label: 'Go to Dashboard' },
  application_received:   { url: DOCUMENTS_URL,                 label: 'View Documents' },
  status_changed:         { url: '/dashboard',                  label: 'View Status' },
  document_approved:      { url: '/documents',                  label: 'View Documents' },
  // A rejected document has to be REPLACED, which is an upload: the Documents page's action-needed list has it.
  document_rejected:      { url: DOCUMENTS_URL,                 label: 'Re-upload' },
  condition_raised:       { url: '/documents',                  label: 'View Documents' },
  // The label is one word because this renders in a narrow pill beside the alert text, where "Upload Documents"
  // wrapped and squashed.
  docs_reminder:          { url: DOCUMENTS_URL,                 label: 'Upload' },
  document_request:       { url: DOCUMENTS_URL,                 label: 'Upload' },
  showing_confirmed:      { url: '/homes',                      label: 'View Homes' },
  offer_update:           { url: '/homes',                      label: 'View Homes' },
  option_period_expiring: { url: '/portal/option-period',       label: 'Complete Tasks Now' },
  clear_to_close:         { url: '/portal/final-review',        label: 'Schedule Closing' },
  closing_scheduled:      { url: '/portal/final-review',        label: 'View Closing Details' },
  wire_instructions:      { url: '/portal/closing-disclosures', label: 'View Wire Instructions' },
  coborrower_pending:     { url: '/dashboard',                  label: 'View Dashboard' },
  // The old ReadyCheck alerts deep-link to the Documents page's open items.
  readycheck_phase1_run:                            { url: DOCUMENTS_URL, label: 'View Documents →' },
  readycheck_income_confirmed:                      { url: DOCUMENTS_URL, label: 'View Documents →' },
  readycheck_income_disputed:                       { url: DOCUMENTS_URL, label: 'View Documents →' },
  readycheck_phase3_credit_pulled:                  { url: DOCUMENTS_URL, label: 'View Documents →' },
  readycheck_submitted:                             { url: '/dashboard',  label: 'View Status →' },
  readycheck_primary_submitted_awaiting_co:         { url: '/dashboard',  label: 'View Status →' },
  readycheck_coborrower_submitted_awaiting_primary: { url: '/dashboard',  label: 'View Status →' },
  readycheck_awaiting_coborrower:                   { url: '/dashboard',  label: 'View Status →' },
  review_section_accepted:                          { url: DOCUMENTS_URL, label: 'View Documents →' },
  review_section_flagged:                           { url: DOCUMENTS_URL, label: 'Address Items →' },
  additional_need:                                  { url: DOCUMENTS_URL, label: 'Address Items →' },
  credit_unlocked:                                  { url: DOCUMENTS_URL, label: 'View Documents →' },
};

function timeAgo(isoString) {
  const now = new Date();
  const then = new Date(isoString);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Alert Detail Modal ─────────────────────────────────────────────────────────
function AlertModal({ alert, onClose }) {
  const navigate = useNavigate();
  const style = ALERT_ICONS[alert.type] || DEFAULT_STYLE;
  // 🔑 THE SERVER'S BUTTON FIRST (Jesse, 2026-10-28 — backend constants/alertDisplay): its label and route — a request on a
  // step the borrower has passed opens that step's row in view mode (/apply/guided?step=N#row), never a move. The old
  // per-type map stays only for a row the server sends no button for.
  const action = alert.route && alert.label ? { url: alert.route, label: alert.label, server: true } : (ALERT_ACTIONS[alert.type] || null);

  // ESC to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleAction = () => {
    onClose();
    if (action?.server) navigate(action.url, { state: { from: '/alerts' } });
    else if (action?.url) navigate(docsPath(action.url));
  };

  return (
    <div
      className="safe-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ animation: 'alertOverlayIn 0.2s ease' }}
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="safe-modal-box relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ animation: 'alertModalIn 0.2s ease', '--sm-max': '90vh' }}
      >
        {/* Header */}
        <div
          className="flex items-start gap-3 px-5 py-4 flex-shrink-0"
          style={{ backgroundColor: style.header }}
        >
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl flex-shrink-0 mt-0.5">
            {style.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-white text-base leading-snug">{alert.title}</h2>
            <p className="text-white/70 text-xs mt-0.5">Received {timeAgo(alert.created_at)}</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors mt-0.5"
          >
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1">
          <p className="text-sm text-gray-700 leading-relaxed">{alert.message}</p>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-full border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          {action && (
            <button
              data-testid="alerts-modal-action"
              onClick={handleAction}
              className="flex-1 h-10 rounded-full text-sm font-semibold text-white flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity"
              style={{ backgroundColor: style.header }}
            >
              <span>{action.label}</span>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes alertOverlayIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes alertModalIn {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

// ── Main Alerts Page ───────────────────────────────────────────────────────────
export default function Alerts() {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  // Returns a sync function; the hook also re-syncs on mount and resume.
  const syncBadge = useAppBadge(token);
  const [selectedAlert, setSelectedAlert] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/alerts?limit=50`, { headers });
      setAlerts(data.alerts || []);
      setUnread(data.unread || 0);
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token) fetchAlerts();
  }, [fetchAlerts, token]);

  // Open modal on click — do NOT mark as read yet
  const handleAlertClick = (alert) => {
    setSelectedAlert(alert);
  };

  // Mark as read + close modal
  const handleCloseAlert = useCallback(async () => {
    if (selectedAlert && !selectedAlert.is_read) {
      // Optimistic update
      setAlerts(prev => prev.map(a => a.id === selectedAlert.id ? { ...a, is_read: 1 } : a));
      setUnread(prev => Math.max(0, prev - 1));
      try {
        await axios.put(`${API_URL}/api/alerts/${selectedAlert.id}/read`, {}, { headers });
        // ⚠️ RE-ASKED, NOT DECREMENTED. The optimistic -1 above is fine for the
        // on-screen number, which the borrower can see and re-load. The app
        // icon cannot be re-loaded by looking at it, so it is synced from the
        // server's own count — the same query the alerts list uses.
        syncBadge();
      } catch { /* ignore */ }
    }
    setSelectedAlert(null);
  }, [selectedAlert, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await axios.put(`${API_URL}/api/alerts/read-all`, {}, { headers });
      setAlerts(prev => prev.map(a => ({ ...a, is_read: 1 })));
      setUnread(0);
      syncBadge();
    } catch { /* ignore */ }
    setMarkingAll(false);
  };

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
            {unread > 0 && (
              <p className="text-sm text-gray-500 mt-0.5">{unread} unread notification{unread !== 1 ? 's' : ''}</p>
            )}
          </div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              disabled={markingAll}
              className="text-sm font-medium text-[#3a6ad9] hover:text-[#2d5ab8] disabled:opacity-50 transition-colors"
            >
              {markingAll ? 'Marking…' : 'Mark all as read'}
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#3a6ad9] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🔔</div>
            <h2 className="text-lg font-semibold text-gray-700 mb-2">No alerts yet</h2>
            <p className="text-sm text-gray-400">We'll notify you here when something needs your attention.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {alerts.map((alert) => {
              const style = ALERT_ICONS[alert.type] || DEFAULT_STYLE;
              const isUnread = !alert.is_read;
              return (
                <button
                  key={alert.id}
                  data-testid="alerts-row"
                  data-type={alert.type}
                  onClick={() => handleAlertClick(alert)}
                  className={`w-full text-left flex items-start gap-4 p-4 rounded-xl border transition-all duration-150 cursor-pointer ${
                    isUnread
                      ? `${style.bg} ${style.border} shadow-sm hover:brightness-95`
                      : 'bg-white border-gray-100 hover:bg-gray-50'
                  }`}
                >
                  {/* Unread dot */}
                  <div className="flex-shrink-0 w-2.5 flex items-center justify-center pt-1.5">
                    {isUnread && <div className={`w-2 h-2 rounded-full ${style.dot}`} />}
                  </div>

                  {/* Icon */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                    isUnread ? 'bg-white shadow-sm' : 'bg-gray-100'
                  }`}>
                    {style.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm leading-tight ${isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-600'}`}>
                        {alert.title}
                      </p>
                      <span className="flex-shrink-0 text-xs text-gray-400 mt-0.5">
                        {timeAgo(alert.created_at)}
                      </span>
                    </div>
                    <p className={`text-sm mt-1 leading-relaxed line-clamp-2 ${isUnread ? 'text-gray-700' : 'text-gray-400'}`}>
                      {alert.message}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Alert detail modal */}
      {selectedAlert && (
        <AlertModal
          alert={selectedAlert}
          onClose={handleCloseAlert}
        />
      )}
    </AppShell>
  );
}
